import { createReducer, on } from '@ngrx/store';

import { AdvancedTargetingDefinition, Quota } from '@ps/survey-utils';

import { INITIAL_SURVEY_CREATION_AUDIENCE_STATE } from '../survey-creation-state.constants';
import { SurveyCreationAudienceState } from '../survey-creation-state.model';
import {
  SurveyCreationActions,
  SurveyCreationApiActions,
  SurveyCreationNavbarApiActions,
} from './survey-creation-audience.actions';

function withoutDefinition(
  definitions: AdvancedTargetingDefinition[],
  removed: AdvancedTargetingDefinition,
): AdvancedTargetingDefinition[] {
  return definitions.filter(
    (definition) => definition.attributeId !== removed.attributeId,
  );
}

// Re-applying the same question replaces its selection rather than adding a
// duplicate card. Quotas are attached separately from the Apply flow, so the
// existing flag is carried over — otherwise editing the targeting values would
// silently drop a definition's quotas.
function upsertDefinition(
  definitions: AdvancedTargetingDefinition[],
  applied: AdvancedTargetingDefinition,
): AdvancedTargetingDefinition[] {
  const existing = definitions.find(
    (definition) => definition.attributeId === applied.attributeId,
  );

  return [
    ...withoutDefinition(definitions, applied),
    {
      ...applied,
      hasQuotasApplied: existing?.hasQuotasApplied ?? applied.hasQuotasApplied,
      quotas: existing?.quotas,
    },
  ];
}

function withAppliedQuotas(
  definitions: AdvancedTargetingDefinition[],
  attributeId: string,
  quotas: Quota[],
): AdvancedTargetingDefinition[] {
  return definitions.map((definition) =>
    definition.attributeId === attributeId
      ? { ...definition, quotas, hasQuotasApplied: true }
      : definition,
  );
}

export const surveyCreationAudienceReducer = createReducer(
  INITIAL_SURVEY_CREATION_AUDIENCE_STATE,

  on(
    SurveyCreationActions.init,
    (state, { surveyId }): SurveyCreationAudienceState => {
      // Already hydrated for this exact survey (e.g. just transitioned from
      // create → edit) — keep the in-memory survey instead of wiping it and
      // forcing loadSurveyForEdit$ to re-fetch what we already have.
      const alreadyHydrated =
        !!surveyId && state.surveyId === surveyId && !!state.survey;

      return alreadyHydrated
        ? state
        : { ...INITIAL_SURVEY_CREATION_AUDIENCE_STATE, surveyId };
    },
  ),

  on(
    SurveyCreationApiActions.loadSurveyForEditSuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      survey,
      error: null,
    }),
  ),

  on(
    SurveyCreationActions.updateMetadata,
    (state, { metadata }): SurveyCreationAudienceState => ({
      ...state,
      metadata,
    }),
  ),

  on(
    SurveyCreationActions.updateQualifications,
    (state, { qualifications }): SurveyCreationAudienceState => ({
      ...state,
      qualifications,
    }),
  ),

  on(
    SurveyCreationActions.updateQuotas,
    (state, { quotas }): SurveyCreationAudienceState => ({
      ...state,
      quotas,
    }),
  ),

  on(
    SurveyCreationActions.toggleLocationQualificationOption,
    (state, { qualificationCode, optionId }): SurveyCreationAudienceState => {
      const selectedOptionIds =
        state.locationSelectionsByQualificationCode[qualificationCode] ?? [];
      const nextOptionIds = selectedOptionIds.includes(optionId)
        ? selectedOptionIds.filter((id) => id !== optionId)
        : [...selectedOptionIds, optionId];

      return {
        ...state,
        locationSelectionsByQualificationCode: {
          ...state.locationSelectionsByQualificationCode,
          [qualificationCode]: nextOptionIds,
        },
        error: null,
      };
    },
  ),

  on(
    SurveyCreationActions.clearLocationQualificationSelections,
    (state): SurveyCreationAudienceState => ({
      ...state,
      locationSelectionsByQualificationCode: {},
      error: null,
    }),
  ),

  on(
    SurveyCreationApiActions.createStandardSurveySuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      surveyId: survey.id,
      survey,
      error: null,
    }),
  ),

  on(
    SurveyCreationApiActions.createStandardSurveyFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  on(
    SurveyCreationApiActions.saveSurveySuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      surveyId: survey.id,
      survey,
      error: null,
    }),
  ),

  on(
    SurveyCreationApiActions.saveSurveyFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  on(
    SurveyCreationApiActions.loadSurveyForEditFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  on(
    SurveyCreationActions.applyAdvancedTargetingDefinition,
    (state, { definition }): SurveyCreationAudienceState => ({
      ...state,
      advancedTargetingDefinitions: upsertDefinition(
        state.advancedTargetingDefinitions,
        definition,
      ),
      error: null,
    }),
  ),

  on(
    SurveyCreationActions.applyAdvancedTargetingQuotas,
    (state, { attributeId, quotas }): SurveyCreationAudienceState => ({
      ...state,
      advancedTargetingDefinitions: withAppliedQuotas(
        state.advancedTargetingDefinitions,
        attributeId,
        quotas,
      ),
      error: null,
    }),
  ),

  on(
    SurveyCreationActions.removeAdvancedTargetingDefinition,
    (state, { definition }): SurveyCreationAudienceState => ({
      ...state,
      advancedTargetingDefinitions: withoutDefinition(
        state.advancedTargetingDefinitions,
        definition,
      ),
      error: null,
    }),
  ),

  on(
    SurveyCreationActions.hydrateAdvancedTargetingDefinitions,
    (state, { definitions }): SurveyCreationAudienceState => ({
      ...state,
      advancedTargetingDefinitions: definitions,
      hasHydratedAdvancedTargeting: true,
      error: null,
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.computeScorePending,
    (state): SurveyCreationAudienceState => ({
      ...state,
      score: {
        ...state.score,
        loading: true,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.computeScoreSuccess,
    (state, { scorePercentage }): SurveyCreationAudienceState => ({
      ...state,
      score: {
        scorePercentage,
        loading: false,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.computeScoreFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      score: {
        scorePercentage: null,
        loading: false,
        error,
      },
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.recalculateFeasibilityPending,
    (state): SurveyCreationAudienceState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        loading: true,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.recalculateFeasibilitySuccess,
    (
      state,
      { total, quotaGroups, audience, hasQuota },
    ): SurveyCreationAudienceState => ({
      ...state,
      feasibility: {
        total,
        quotaGroups,
        audience,
        hasQuota,
        loading: false,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.recalculateFeasibilityFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        loading: false,
        error,
      },
    }),
  ),

  on(
    SurveyCreationActions.reset,
    (): SurveyCreationAudienceState => INITIAL_SURVEY_CREATION_AUDIENCE_STATE,
  ),
);
